/**
 * JSON Exporter
 * Export complete app data to JSON format
 */

import { getAllAccounts, getGoalsByAccountId } from '../storage/GoalStorage';
import { replayCompletionEvents, getAllEvents } from '../storage/CompletionLog';
import { formatDateToISO } from '../../utils/dateUtils';

interface ExportData {
  version: string;
  exported_at: string;
  device_id: string;
  accounts: Array<{
    id: string;
    name: string;
    created_at: string;
    sync_enabled: boolean;
    goals: Array<{
      id: string;
      name: string;
      type: 'main' | 'side';
      created_at: string;
    }>;
    completions: Array<{
      id: string;
      goal_id: string;
      date: string;
      completed: boolean;
      timestamp: number;
    }>;
  }>;
  event_log: Array<{
    id: string;
    timestamp: number;
    account_id: string;
    type: string;
    payload: Record<string, any>;
  }>;
}

/**
 * Export all data to JSON format
 */
export const exportToJSON = async (): Promise<string> => {
  const exportData: ExportData = {
    version: '1.0',
    exported_at: new Date().toISOString(),
    device_id: '',  // Will be set from event log
    accounts: [],
    event_log: [],
  };

  // Get all accounts
  const accounts = await getAllAccounts();

  for (const account of accounts) {
    // Get all goals for this account
    const goals = await getGoalsByAccountId(account.id);

    // Get all completions for all goals
    const allCompletions: any[] = [];

    for (const goal of goals) {
      const completionMap = await replayCompletionEvents(account.id, goal.id);
      const completions = Array.from(completionMap.values());

      allCompletions.push(
        ...completions.map(c => ({
          id: c.id,
          goal_id: c.goalId,
          date: c.date,
          completed: c.completed,
          timestamp: c.timestamp,
        }))
      );
    }

    // Sort completions by date (most recent first)
    allCompletions.sort((a, b) => b.date.localeCompare(a.date));

    // Add account data
    exportData.accounts.push({
      id: account.id,
      name: account.name,
      created_at: new Date(account.createdAt).toISOString(),
      sync_enabled: account.syncEnabled,
      goals: goals.map(g => ({
        id: g.id,
        name: g.name,
        type: g.type,
        created_at: new Date(g.createdAt).toISOString(),
      })),
      completions: allCompletions,
    });
  }

  // Get full event log
  const events = await getAllEvents();
  exportData.event_log = events.map(e => ({
    id: e.id,
    timestamp: e.timestamp,
    account_id: e.accountId,
    type: e.type,
    payload: e.payload,
  }));

  // Set device ID from first event (if available)
  if (events.length > 0) {
    exportData.device_id = events[0].deviceId;
  }

  return JSON.stringify(exportData, null, 2);
};

/**
 * Generate JSON filename with current date
 */
export const generateJSONFilename = (): string => {
  const today = formatDateToISO(new Date());
  return `everydaygoal-export-${today}.json`;
};
